/**
 * aurora3DPipeline — volumetric 3D aurora borealis.
 *
 * 6 layered aurora ribbons evaluated along a 3D ray. Each ribbon
 * is a curl-noise-perturbed sheet with audio-reactive intensity,
 * driving them brighter on bass. Includes starfield, mountain
 * silhouette, and a moon for context.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface Aurora3DPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}

float hash21(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p.yx + 19.19);
  return fract((p.x + p.y) * p.x);
}

vec2 hash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash21(i + vec2(0.0, 0.0));
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm2(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 5; i++) {
    v += amp * noise2(p);
    p *= 2.03;
    amp *= 0.5;
  }
  return v;
}

// Aurora ribbon: sample a horizontal slab of curl-noise noise.
float ribbon(vec3 p, float t, float seed, float thick) {
  // Sheet of noise at height y. Lower frequency in y, higher in x,z.
  float n1 = fbm2(vec2(p.x * 1.2 + t * 0.1 + seed, p.z * 1.0 + seed * 1.3));
  float n2 = fbm2(vec2(p.x * 0.7 - t * 0.05 + seed, p.z * 0.8 - seed * 0.7));
  // Vertical wind shear — the sheet drifts upward over time.
  float sheet = abs(p.y - (0.5 + 0.4 * n1 - 0.3 * n2));
  // Boundary softness from second derivative (gradient).
  float windGrad = length(vec2(n1 - n2, n1 * n2));
  return exp(-sheet * sheet / (thick * thick * (1.0 + 2.0 * windGrad)));
}

vec3 auroraColor(vec3 p, float t) {
  // Bands: green base, magenta tip, blue root. Smoothly vary with height.
  vec3 base = vec3(0.30, 0.95, 0.50);
  vec3 mid = vec3(0.15, 0.55, 0.95);
  vec3 tip = vec3(0.85, 0.30, 0.95);
  float h = clamp(p.y * 0.8, 0.0, 1.0);
  vec3 c1 = mix(base, mid, smoothstep(0.0, 0.6, h));
  return mix(c1, tip, smoothstep(0.6, 1.0, h));
}

float stars(vec3 rd) {
  vec3 q = rd * 60.0;
  vec3 i = floor(q);
  vec3 f = fract(q);
  f = f * f * (3.0 - 2.0 * f);
  float h = hash21(i.xy + i.z * 17.0);
  float star = pow(h, 20.0);
  // Twinkle.
  star *= 0.5 + 0.5 * sin(uTime * 2.0 + h * 30.0);
  return star;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;
  p.y = -p.y;

  // Camera looks along -z, slight orbit around y.
  float ang = uTime * 0.05;
  vec3 rd = normalize(vec3(p.x, p.y * 0.6, -1.0));
  // Rotate rd slightly around y to give parallax.
  float ca = cos(ang), sa = sin(ang);
  rd.xz = mat2(ca, -sa, sa, ca) * rd.xz;

  // Sky gradient: deep blue up to magenta horizon.
  vec3 skyUp = vec3(0.04, 0.05, 0.16);
  vec3 skyHorizon = vec3(0.10, 0.05, 0.20);
  vec3 col = mix(skyHorizon, skyUp, smoothstep(0.0, 0.7, rd.y));
  col += vec3(0.04, 0.02, 0.12) * smoothstep(-0.3, -0.1, rd.y);
  // Stars above horizon.
  if (rd.y > 0.0) {
    col += vec3(1.0, 0.95, 0.85) * stars(rd);
  }

  // Mountain silhouette.
  float mountain = 0.0;
  {
    vec2 mq = vec2(rd.x * 4.0 + uTime * 0.05, rd.y * 2.0);
    float mn = fbm2(mq);
    float mh = -0.25 + 0.3 * mn - 0.05 * sin(mq.x * 0.7);
    float d = rd.y - mh;
    float mask = 1.0 - smoothstep(0.0, 0.05, d);
    col = mix(col, vec3(0.05, 0.04, 0.10), mask);
    mountain = mask;
  }

  // Aurora: 6 ribbons stacked vertically with shifts.
  vec3 aurora = vec3(0.0);
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    // Ribbons at increasing heights with different thickness/seeds.
    float baseY = 0.05 + fi * 0.10;
    float thick = 0.06 + 0.04 * sin(uTime * 0.3 + fi * 1.7);
    // Shift the slab center over time (wind blowing north).
    vec3 rp = rd;
    rp.y -= baseY;
    rp.x += sin(uTime * 0.2 + fi) * 0.3;
    float d = ribbon(rp, uTime, fi * 7.31, thick);
    vec3 ribbonColor = auroraColor(rd, uTime);
    // Brighten by bass.
    d *= 0.4 + 1.2 * (0.5 + bass * 0.7);
    aurora += ribbonColor * d;
  }
  // Aurora only above horizon.
  col = mix(col, col + aurora, smoothstep(-0.15, 0.05, rd.y));

  // Stars back over the aurora (high freq detail).
  col += vec3(1.0, 0.95, 0.85) * stars(rd) * 0.5;

  // Pulse with mid.
  col *= 1.0 + mid * 0.3;
  col += vec3(0.10, 0.30, 0.45) * mid * 0.15;
  // High twinkle added as faint sparkles.
  col += vec3(0.95, 0.65, 0.85) * high * 0.2;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.25;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function createAurora3DPipeline(gl: WebGL2RenderingContext): Aurora3DPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}

export function drawAurora3D(
  gl: WebGL2RenderingContext, pipe: Aurora3DPipeline,
  width: number, height: number, t: number,
): void {
  gl.useProgram(pipe.program);
  const p = pipe.program;
  gl.uniform2f(gl.getUniformLocation(p, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(p, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(p, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(p, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(p, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}

export function computeAurora3DBands(t: number, out: Float32Array): void {
  out[0] = 0.4 + 0.4 * Math.sin(t * 0.4);
  out[1] = 0.4 + 0.4 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.4 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.4 + 0.4 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.4 + 0.4 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.4 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.4 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.4 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.4 + 0.4 * Math.sin(t * 2.8 + 3.0);
}

export function computeAurora3DRealtimeBands(
  freqData: Uint8Array, out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let sum = 0, count = 0;
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    for (let k = start; k < end; k++) { sum += freqData[k]; count++; }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}
